export type AvatarLatencyProvider = "liveavatar" | "did";
export type AvatarLatencyInputMode = "voice" | "text";
export type AvatarLatencySource = "prod" | "benchmark";

/** Client-Messpunkte in ms relativ zu inputSubmitted (Aufnahme-Stopp bzw. Text-Senden), gemessen mit performance.now(). */
export type AvatarLatencyClientMarks = {
    /** Sprechende (VAD) bis Aufnahme-Stopp – nur bei Spracheingabe. */
    silenceWait?: number;
    sttDone?: number;
    chatSent?: number;
    firstRunUpdate?: number;
    runComplete?: number;
    didSpeakReceived?: number;
    providerSpeakStart?: number;
    audioAudible?: number;
};

/** Server-Messpunkte in ms relativ zum Eingang des Chat-Requests. Nie mit Client-Werten verrechnen (andere Uhr). */
export type AvatarLatencyServerMarks = {
    llmRequest?: number;
    llmFirstToken?: number;
    llmComplete?: number;
    ttsStart?: number;
    ttsFirstByte?: number;
    firstPacketSent?: number;
    speakStarted?: number;
    didSpeakEmitted?: number;
};

export const AVATAR_LATENCY_CLIENT_MARK_KEYS: (keyof AvatarLatencyClientMarks)[] = [
    "silenceWait", "sttDone", "chatSent", "firstRunUpdate", "runComplete", "didSpeakReceived", "providerSpeakStart", "audioAudible"
];

export type AvatarLatencyTurn<ID = string, DATE = string | Date> = {
    _id?: ID;
    turnId: string;
    botId: string;
    org: string;
    created: DATE;
    provider?: AvatarLatencyProvider;
    inputMode?: AvatarLatencyInputMode;
    source?: AvatarLatencySource;
    interrupted?: boolean;
    client?: AvatarLatencyClientMarks;
    server?: AvatarLatencyServerMarks;
    answerChars?: number;
    toolRounds?: number;
    docSearchUsed?: boolean;
    llmModel?: string;
    ttsModel?: string;
};

export type AvatarLatencyClientReport = {
    turnId: string;
    provider: AvatarLatencyProvider;
    inputMode: AvatarLatencyInputMode;
    source: AvatarLatencySource;
    interrupted: boolean;
    client: AvatarLatencyClientMarks;
};

export type AvatarLatencyDetailPhaseKey =
    | "silenceWait" | "stt" | "prep" | "llmFirstToken" | "llmRest" | "speechGen" | "avatarStart" | "rest";
export type AvatarLatencyMainPhaseKey = "input" | "ai" | "speech" | "avatar";
export type AvatarLatencyPhaseKey = AvatarLatencyDetailPhaseKey | AvatarLatencyMainPhaseKey;

export const AVATAR_LATENCY_DETAIL_PHASES: AvatarLatencyDetailPhaseKey[] = [
    "silenceWait", "stt", "prep", "llmFirstToken", "llmRest", "speechGen", "avatarStart", "rest"
];
export const AVATAR_LATENCY_MAIN_PHASES: AvatarLatencyMainPhaseKey[] = ["input", "ai", "speech", "avatar"];

const MAIN_PHASE_PARTS: Record<AvatarLatencyMainPhaseKey, AvatarLatencyDetailPhaseKey[]> = {
    input: ["silenceWait", "stt"],
    ai: ["prep", "llmFirstToken", "llmRest"],
    speech: ["speechGen"],
    avatar: ["avatarStart", "rest"]
};

export type AvatarLatencyTurnPhases = {
    /** Aufnahme-Stopp bzw. Text-Senden bis hörbares Audio. */
    systemMs: number;
    /** Sprechende (VAD) bzw. Text-Senden bis hörbares Audio. */
    perceivedMs: number;
    detail: Record<AvatarLatencyDetailPhaseKey, number>;
};

const diff = (to: number | undefined, from: number | undefined): number =>
    to === undefined || from === undefined ? 0 : Math.max(0, to - from);

/** Zerlegt einen Turn in Phasen; null, wenn Client- oder Serverteil für eine Auswertung fehlen. */
export const computeAvatarLatencyPhases = (turn: AvatarLatencyTurn<unknown, unknown>): AvatarLatencyTurnPhases | null => {
    const c = turn.client ?? {};
    const s = turn.server ?? {};
    const systemMs = c.audioAudible ?? c.providerSpeakStart;
    if (systemMs === undefined || s.llmComplete === undefined) return null;

    const silenceWait = turn.inputMode === "voice" ? (c.silenceWait ?? 0) : 0;
    const clientSpeechStart = Math.min(c.providerSpeakStart ?? systemMs, systemMs);
    const speechGen = turn.provider === "did"
        ? diff(clientSpeechStart, c.didSpeakReceived)
        : diff(s.firstPacketSent, s.llmComplete);

    const detail: Record<AvatarLatencyDetailPhaseKey, number> = {
        silenceWait,
        stt: turn.inputMode === "voice" ? (c.sttDone ?? 0) : 0,
        prep: s.llmRequest ?? 0,
        llmFirstToken: diff(s.llmFirstToken, s.llmRequest),
        llmRest: diff(s.llmComplete, s.llmFirstToken ?? s.llmRequest),
        speechGen,
        avatarStart: diff(systemMs, clientSpeechStart),
        rest: 0
    };
    const measured = detail.stt + detail.prep + detail.llmFirstToken + detail.llmRest + detail.speechGen + detail.avatarStart;
    detail.rest = Math.max(0, systemMs - measured);

    return { systemMs, perceivedMs: systemMs + silenceWait, detail };
};

export const avatarLatencyMainPhase = (detail: Record<AvatarLatencyDetailPhaseKey, number>, key: AvatarLatencyMainPhaseKey): number =>
    MAIN_PHASE_PARTS[key].reduce((sum, part) => sum + detail[part], 0);

/** Perzentil mit linearer Interpolation (p in 0..100). */
export const percentile = (values: number[], p: number): number => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const idx = (p / 100) * (sorted.length - 1);
    const lo = sorted[Math.floor(idx)] ?? 0;
    const hi = sorted[Math.ceil(idx)] ?? lo;
    return Math.round(lo + (hi - lo) * (idx - Math.floor(idx)));
};

export type AvatarLatencyStat = { p50: number; p90: number };

export type AvatarLatencyTurnSummary = {
    turnId: string;
    created: string;
    provider?: AvatarLatencyProvider;
    inputMode?: AvatarLatencyInputMode;
    answerChars?: number;
    toolRounds?: number;
    docSearchUsed?: boolean;
    llmModel?: string;
    ttsModel?: string;
    systemMs: number;
    perceivedMs: number;
    detail: Record<AvatarLatencyDetailPhaseKey, number>;
};

export type AvatarLatencySummary = {
    count: number;
    perceived: AvatarLatencyStat | null;
    system: AvatarLatencyStat | null;
    /** Anteil der Turns mit gefühlter Wartezeit unter 3 s (0..1). */
    under3sShare: number | null;
    /** Detailphasen; Hauptphasen berechnet der Client bei Bedarf aus `turns`. */
    phases: { key: AvatarLatencyDetailPhaseKey; p50: number; p90: number }[];
    turns: AvatarLatencyTurnSummary[];
};

const stat = (values: number[]): AvatarLatencyStat | null =>
    values.length === 0 ? null : { p50: percentile(values, 50), p90: percentile(values, 90) };

export const summarizeAvatarLatency = (turns: AvatarLatencyTurn<unknown>[]): AvatarLatencySummary => {
    const rows = turns
        .map(turn => ({ turn, phases: computeAvatarLatencyPhases(turn) }))
        .filter((r): r is { turn: AvatarLatencyTurn<unknown>; phases: AvatarLatencyTurnPhases } => r.phases !== null);

    const perceived = rows.map(r => r.phases.perceivedMs);
    const phases = AVATAR_LATENCY_DETAIL_PHASES.map(key => {
        const values = rows.map(r => r.phases.detail[key]);
        return { key, p50: percentile(values, 50), p90: percentile(values, 90) };
    });

    return {
        count: rows.length,
        perceived: stat(perceived),
        system: stat(rows.map(r => r.phases.systemMs)),
        under3sShare: rows.length === 0 ? null : perceived.filter(v => v < 3000).length / rows.length,
        phases,
        turns: rows.map(({ turn, phases: p }) => ({
            turnId: turn.turnId,
            created: new Date(turn.created).toISOString(),
            provider: turn.provider,
            inputMode: turn.inputMode,
            answerChars: turn.answerChars,
            toolRounds: turn.toolRounds,
            docSearchUsed: turn.docSearchUsed,
            llmModel: turn.llmModel,
            ttsModel: turn.ttsModel,
            systemMs: p.systemMs,
            perceivedMs: p.perceivedMs,
            detail: p.detail
        }))
    };
};
